<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, nextTick } from 'vue'
import router from '@/router'
import axios from 'axios'
import SnappyJS from 'snappyjs'
import { useRoute } from 'vue-router'
import { calculateArticleList } from '@/utils/articleList'
import { getArticleList, getLegacyArticleList } from '@/api/http'
import { getHeadsetType, getMarkLogs, getSupportAreaCode, getSupportPath } from '@/utils/common'
import { isLegacySeeMoreLink } from '@/utils/supportLegacy'
import { useInternationalStore } from '@/store/international'
import { useI18n } from 'vue-i18n'

const internationalStore = useInternationalStore()
const { t } = useI18n()
const route = useRoute()
const timer: any = ref(null)
const timer2: any = ref(null)
const skeleton = ref(true)
const loading = ref(false)
const queryTitle = route.query.title
const queryType = route.query.type?.replace(/\s/g, '')
/*
 * 判断当前 seeMore 是否是旧链接
 * 旧 seeMore 链接保留旧列表详情样式，但数据源切到旧接口
 */
const legacySeeMore = computed(() => isLegacySeeMoreLink(route.query))

const articleList = ref([])
const detailInfo: any = ref({})
// 获取已存文章列表
// let storageArticleList = JSON.parse(sessionStorage.getItem('articleList') || '[]')
// 动态获取文章类型
const articleType = computed(() => {
  const queryArticleType = (route.query.articleType || route.query.activeType) as string
  if (queryArticleType === 'service_manual') return t('ContentType.guide')
  if (queryArticleType === 'troubleshooting') return t('ContentType.troubleshooting')
  // 1、如果当前是日文环境，直接返回查询参数
  if (internationalStore.location.abb === 'jp' || internationalStore.location.abb === 'ja') {
    return queryArticleType
  }

  // 2、如果当前是英文环境，需要处理日文参数转换为对应的英文
  if (internationalStore.location.abb === 'en') {
    // 日文到英文的映射
    const jpToEnMap = {
      ガイド: 'Guide',
      トラブル対応: 'Troubleshooting'
    }

    // 如果查询参数是日文，返回对应的英文
    if (jpToEnMap[queryArticleType]) {
      return jpToEnMap[queryArticleType]
    }

    // 否则直接返回查询参数
    return queryArticleType
  }

  // 3、默认直接返回查询参数
  return queryArticleType
})

onMounted(() => {
  getArticleData()
  document.body.addEventListener('click', toPath)
  timer.value = setTimeout(() => {
    skeleton.value = false
  }, 500)
  console.log('route', route.query)
})

const activeType = ref('')
const actived: any = ref(null)
const articleTitle = ref('')
const valueHtml = ref('')
// 图片预览相关
const showImagePreview = ref(false)
const previewImageUrl = ref('')

/*
 * 获取文章数据
 * @description 旧 seeMore 使用旧接口，新 seeMore 使用 v2 接口
 */
const getArticleData = () => {
  // 判断已存文章列表中是否存在 id 等于 route.query.id 的元素
  // const hasMatchingId = storageArticleList.some((item) => {
  //   return item.list && item.list.some((subItem) => subItem.id === route.query.id)
  // })
  // // 判断sessionStorage中是否存在articleList
  // if (storageArticleList.length !== 0 && hasMatchingId) {
  //   articleList.value = storageArticleList
  //   listenClick()
  // } else {
  //   const param = {
  //     headset_model: route.query.headsetType
  //   }
  //   getArticleList(param).then((res: any) => {
  //     console.log('详情页获取分类数据', res)
  //     calculateArticleList(res, route.query.articleType as string)
  //     articleList.value = calculateArticleList(res, route.query.articleType as string)
  //     sessionStorage.setItem('articleList', JSON.stringify(articleList.value))
  //     listenClick()
  //   })
  // }

  const param = {
    headset_model: legacySeeMore.value ? route.query.headsetType : getHeadsetType(route.params.product as string),
    area_code: getSupportAreaCode(internationalStore.location.abb, route.path)
  }
  /*
   * 选择服务支持接口
   * 旧链接按原 headsetType 请求旧接口，新链接继续按产品路由请求 v2
   */
  const requestArticleList = legacySeeMore.value ? getLegacyArticleList : getArticleList
  requestArticleList(param).then((res: any) => {
    console.log('详情页获取分类数据', res)
    articleList.value = calculateArticleList(res, articleType.value)
    listenClick()
  }).catch(() => {
    articleList.value = []
    valueHtml.value = ''
    articleTitle.value = ''
    skeleton.value = false
    loading.value = false
  })
}

/*
 * 页面渲染后自动触发菜单展开
 * @description 优先展开链接中指定分类和文章，找不到时展示第一篇文章
 */
const listenClick = () => {
  timer2.value = setTimeout(() => {
    const openFirstArticle = () => {
      const firstGroup = articleList.value[0]
      const firstArticle = firstGroup?.list?.[0]
      if (!firstGroup || !firstArticle) {
        valueHtml.value = ''
        articleTitle.value = ''
        loading.value = false
        return
      }
      detailInfo.value = firstArticle
      clickMenu(firstGroup.type.replace(/\s/g, ''), firstGroup.list.length + 1)
      getEditorData(firstArticle.url, firstArticle.title, firstArticle.id)
    }

    console.log('queryType: ', queryType)
    if (queryType) {
      const index = articleList.value.findIndex(item => item.type.replace(/\s/g, '') === queryType)
      console.log('index: ', index)
      if (index > -1) {
        // 是否存在当前文章
        let isExistCurArticle = articleList.value[index].list.some(item => item.id === route.query.id)
        console.log('isExistCurArticle: ', isExistCurArticle)
        if (isExistCurArticle) {
          // 若存在，点击当前文章
          clickMenu(queryType, articleList.value[index].list.length + 1)
          checkDetailInfo(route.query.id)
        } else {
          // 若不存在，默认点击当前文章列表第一条
          openFirstArticle()
        }
      } else {
        // 若不存在，默认点击当前文章列表第一条
        openFirstArticle()
      }
    } else {
      // 若不存在，默认点击当前文章列表第一条
      openFirstArticle()
    }
  }, 1000)
}

/*
 * 根据 id 获取详情信息
 * @param id - 文章 id
 */
const checkDetailInfo = (id: string) => {
  const index = articleList.value.findIndex(item => item.type.replace(/\s/g, '') === queryType)
  if (index < 0) {
    valueHtml.value = ''
    articleTitle.value = ''
    return
  }
  const checkMenu = articleList.value[index].list
  checkMenu.forEach(item => {
    if (item.id === id) {
      detailInfo.value = item
    }
  })
  getEditorData(detailInfo.value.url, detailInfo.value.title, detailInfo.value.id)
}

/*
 * 处理文章内链接跳转
 * @param e - 点击事件对象
 */
const toPath = (e: any) => {
  if (e.target.tagName === 'A') {
    e.preventDefault()
    window.open(e.target.href, '_blank') // 新页面打开
  } else {
    let checkUrl = e.target.href && e.target.href.split('/#/')[0]
    if (checkUrl && checkUrl.includes('viaim.ai')) {
      window.location.href = e.target.href
      window.location.reload()
    } else {
      e.target.target = '_blank'
    }
  }
}
const clickMenu = (id: string, num: number) => {
  if (activeType.value === id) activeType.value = ''
  else activeType.value = id
}

/*
 * 编辑-回显数据
 * @param url - 文章 url
 * @param title - 文章标题
 * @param id - 文章 id
 */
const getEditorData = (url: string, title: string, id?: string) => {
  console.log('url', url, 'id', id)

  // 发现页埋点配置
  if (id) getMarkLogs(id)
  const timestamp = new Date().getTime()
  if (id === actived.value) return
  actived.value = id
  articleTitle.value = title

  // 更新路由参数，将当前选中的文章详情设置到路由中
  if (id) {
    router.replace({
      path: route.path,
      query: {
        ...route.query,
        type: activeType.value,
        id: id
      }
    })
  }

  if (url) {
    loading.value = true
    axios({
      method: 'get',
      url: `${url}?t=${timestamp}`
    }).then(function (res) {
      if (res?.data) {
        valueHtml.value = normalizeEditorData(res.data)
        // 等待DOM更新后，给图片添加点击事件
        nextTick(() => {
          initImagePreview()
        })
      } else valueHtml.value = ''
    }).catch(() => {
      valueHtml.value = ''
    }).finally(() => {
      loading.value = false
    })
  } else valueHtml.value = ''
}

const normalizeEditorData = (data: unknown) => {
  if (typeof data !== 'string') return ''
  try {
    return uncompressed(data)
  } catch (err) {
    return data
  }
}

// 解压
const uncompressed = data => {
  let values = atob(data).split(',')
  const len = values.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = parseInt(values[i], 10)
  }
  const decoder = new TextDecoder()
  const arrayBuffer = SnappyJS.uncompress(bytes)
  const jsonString = decoder.decode(arrayBuffer)
  let newJsonString = jsonString.replace(/target="_blank"/g, 'target="_top" @click="toPath"')
  return newJsonString
}

/*
 * 初始化图片预览功能
 */
const initImagePreview = () => {
  const editorElement = document.querySelector('.wangEditor-pc')
  if (!editorElement) return

  // 获取所有图片元素
  const images = editorElement.querySelectorAll('img:not([data-preview-initialized])')
  images.forEach(img => {
    // 标记已初始化，避免重复绑定
    img.setAttribute('data-preview-initialized', 'true')

    // 添加点击事件
    img.style.cursor = 'pointer'
    img.addEventListener('click', e => {
      e.preventDefault()
      e.stopPropagation()
      openImagePreview((img as HTMLImageElement).src)
    })
  })
}

/*
 * 打开图片预览
 * @param imageUrl - 预览图片地址
 */
const openImagePreview = (imageUrl: string) => {
  previewImageUrl.value = imageUrl
  showImagePreview.value = true
  // 禁止背景滚动
  document.body.style.overflow = 'hidden'
  // 添加 ESC 键监听
  document.addEventListener('keydown', handleEscapeKey)
}

/*
 * 处理 ESC 键关闭预览
 * @param e - 键盘事件对象
 */
const handleEscapeKey = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && showImagePreview.value) {
    closeImagePreview()
  }
}

/*
 * 关闭图片预览
 */
const closeImagePreview = () => {
  showImagePreview.value = false
  previewImageUrl.value = ''
  // 恢复背景滚动
  document.body.style.overflow = ''
  // 移除 ESC 键监听
  document.removeEventListener('keydown', handleEscapeKey)
}

onUnmounted(() => {
  clearTimeout(timer.value)
  clearTimeout(timer2.value)
  timer.value = null
  timer2.value = null
  document.body.removeEventListener('click', toPath)
  // 移除 ESC 键监听
  document.removeEventListener('keydown', handleEscapeKey)
  // 恢复背景滚动
  document.body.style.overflow = ''
})
</script>

<template>
  <main class="seeMore">
    <div class="breadcrumb">
      <ul class="breadcrumb">
        <li class="pointer" @click="router.push('/support/index')">{{ $t('Support.product.home') }}</li>
        &nbsp;&nbsp;>&nbsp;&nbsp;
        <li class="pointer" @click="router.back()">viaim {{ getSupportPath(route.params.product as string) }} {{ $t('Support.product.support') }}</li>
        &nbsp;&nbsp;>&nbsp;&nbsp;
        <li>{{ $t('Support.product.inst') }}</li>
      </ul>
    </div>
    <div class="main">
      <!-- <div class="Guide-title">VIAIM {{ getSupportPath(route.params.product as string) }} {{ articleType }}</div> -->
      <div class="content">
        <!-- 菜单 -->
        <el-skeleton animated class="aside" :loading="skeleton">
          <template #template>
            <el-skeleton-item variant="caption" class="skeleton-Guide-title" />
            <el-skeleton-item variant="caption" class="skeleton-ul" v-for="item in 9" :key="item" />
          </template>
          <template #default>
            <div class="left-aside">
              <div class="Guide-title">{{ articleType }}</div>
              <aside class="aside">
                <ul class="ul" v-for="item in articleList" :key="item.type" :id="item.type?.replace(/\s/g, '')">
                  <div class="group" @click="clickMenu(item.type?.replace(/\s/g, ''), item.list.length + 1)">
                    {{ item.type }}
                    <svg class="iconpark-icon" :class="{ 'selected-icon': item.type?.replace(/\s/g, '') === activeType }">
                      <use href="#Property1Variant4"></use>
                    </svg>
                  </div>
                  <!-- 展开内容 -->
                  <el-collapse-transition>
                    <div v-show="activeType === item.type?.replace(/\s/g, '')">
                      <li class="li" :class="{ selected: actived === item2.id }" v-for="item2 in item.list" :key="item2.id"
                        @click="getEditorData(item2.url, item2.title, item2.id)">
                        <div>
                          {{ item2.title }}
                        </div>
                      </li>
                    </div>
                  </el-collapse-transition>
                </ul>
              </aside>
            </div>
          </template>
        </el-skeleton>
        <!-- 内容 -->
        <el-skeleton animated class="skeleton-article" :loading="loading">
          <template #template>
            <div class="html">
              <el-skeleton-item variant="h1" class="skeleton-h2" />
              <el-skeleton-item variant="h3" class="skeleton-h3" />
              <el-skeleton-item variant="rect" />
              <el-skeleton-item variant="rect" />
              <el-skeleton-item variant="rect" style="width: 50%" />
              <el-skeleton-item variant="rect" class="skeleton-text" />
              <el-skeleton-item variant="rect" style="width: 50%" />
              <el-skeleton-item variant="h3" class="skeleton-h3" />
              <el-skeleton-item variant="rect" />
              <el-skeleton-item variant="rect" />
              <el-skeleton-item variant="rect" />
              <el-skeleton-item variant="rect" style="width: 50%" />
            </div>
          </template>
          <template #default>
            <article class="article">
              <div class="html">
                <h2>{{ articleTitle }}</h2>
                <div class="wangEditor-pc" v-html="valueHtml"></div>
              </div>
            </article>
          </template>
        </el-skeleton>
      </div>
    </div>
    <!-- 图片预览蒙层 -->
    <div v-if="showImagePreview" class="image-preview-overlay">
      <div class="image-preview-container" @click.stop>
        <button class="image-preview-close" @click="closeImagePreview">
          <img class="close-img" src="https://viaim-h5.oss-ap-southeast-1.aliyuncs.com/iflybuds-website-world/images/common/close-icon.png" />
        </button>
        <img :src="previewImageUrl" alt="预览图片" class="preview-image" />
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.seeMore {
  width: 100%;
  height: calc(100vh - 15.8rem);
  display: flex;
  align-items: center;
  flex-direction: column;
  background-color: #fff;

  .breadcrumb {
    width: 100%;
    padding: 0 1.2rem;
    display: flex;
    justify-content: center;
    background-color: #f2f2f2;

    ul {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      max-width: 128rem;
      height: 5.6rem;
      color: #575757;
      font-size: 1.5rem;
      font-style: normal;
      font-weight: 400;
      line-height: 2rem;
    }
  }

  .main {
    width: 100%;
    max-width: 128rem;
    height: calc(100% - 5.6rem);
    padding: 0 1.2rem 0 1.2rem;

    .left-aside {
      width: 100%;
      height: 100%;
      max-width: 39.4rem;
    }

    .skeleton-Guide-title {
      width: 100%;
      max-width: 39.4rem;
      height: 5.6rem;
      margin: 4.5rem 0 2rem 0;
    }

    .skeleton-ul {
      width: 100%;
      max-width: 39.4rem;
      height: 6rem;
      margin: 0.4rem 0;
    }

    .Guide-title {
      color: #717171;
      font-size: 2.8rem;
      font-style: normal;
      font-weight: 300;
      line-height: normal;
      margin: 4.5rem 0 2rem 2.9rem;
    }

    .content {
      height: 100%;
      display: flex;
      transition: all 0.2s linear;

      .aside {
        width: 100%;
        max-width: 39.4rem;
        height: calc(100% - 11rem);
        overflow-y: auto;
        padding-right: 6px;
        cursor: pointer;
        transition: all 0.2s linear;

        .ul {
          transition: all 0.2s ease-in-out;
          overflow: hidden;
          user-select: none;

          .group {
            width: 100%;
            height: 6rem;
            padding: 0 2.9rem;
            border-radius: 0.5rem;
            background: #f2f2f2;
            color: #000;
            font-size: 2rem;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: 0.4rem 0;

            .selected-icon {
              transform: rotate(90deg);
            }
          }

          .li {
            width: 100%;
            padding: 1.6rem 2.9rem 1.6rem 4.8rem;
            border-radius: 0.5rem;
            color: #000;
            font-size: 2rem;
            font-style: normal;
            font-weight: 400;
            border: 1px solid #fff;
            display: flex;
            align-items: center;

            div {
              display: -webkit-box;
              overflow: hidden;
              text-overflow: ellipsis;
              -webkit-line-clamp: 2; // 几行省略
              -webkit-box-orient: vertical;
              width: 100%;
            }
          }

          .selected {
            border: 1px solid #dbdbdb;
          }

          .li:hover {
            border: 1px solid #dbdbdb;
          }
        }
      }

      .skeleton-article {
        display: flex;
        flex-direction: column;
        // align-items: center;
        width: 100%;
        max-width: 60.6rem;
        height: calc(100vh - 30.1rem);
        overflow-y: auto;
        padding: 0 2.4rem 0 6.4rem;

        .skeleton-html {
          width: 100%;
          max-width: 60.6rem;
          overflow-x: auto;
        }

        .skeleton-h2 {
          margin-top: 4.5rem;
          width: 15.2rem;
          height: 5.2rem;
        }

        .skeleton-h3 {
          margin: 5rem 0 2rem 0;
          width: 14rem;
          height: 3rem;
          display: block;
        }

        .skeleton-text {
          margin-top: 3rem;
        }
      }

      .article {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        overflow-y: auto;

        ::-webkit-scrollbar-track-piece {
          background: #ebebeb;
        }

        ::-webkit-scrollbar {
          width: 0px;
        }

        ::-webkit-scrollbar-thumb {
          background: #c9c9c9;
          border-radius: 0px;
        }

        h2 {
          margin-top: 4.5rem;
          color: #202020;
          font-family: PingFang SC;
          font-size: 38px;
          font-style: normal;
          font-weight: 600;
          line-height: normal;
        }

        h3 {
          margin-top: 46px;
          color: #202020;
          font-size: 22px;
          font-style: normal;
          font-weight: 600;
          line-height: normal;
        }

        .text {
          margin-top: 16px;
          color: #6f6f6f;
          font-size: 16px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
        }

        .html {
          width: 100%;
          max-width: 65.6rem;
          overflow-x: auto;
          padding: 0 2.4rem 0 6.4rem;

          h2 {
            margin-bottom: 14px;
          }

          .wangEditor-pc :deep(h1) {
            font-size: 20px;
          }

          .wangEditor-pc :deep(h2) {
            font-size: 15px;
            font-weight: normal;
          }

          .wangEditor-pc :deep(h3) {
            font-size: 15px;
            font-weight: normal;
          }

          .wangEditor-pc :deep(h4) {
            font-size: 13px;
            font-weight: normal;
          }

          .wangEditor-pc :deep(h5) {
            font-size: 16px;
            font-weight: normal;
          }

          .wangEditor-pc :deep(p) {
            line-height: 1.8;
          }

          .wangEditor-pc :deep(a) {
            color: #1877f2;
          }

          .wangEditor-pc :deep(table) {
            border-collapse: collapse;
            border: 1px solid #ccc;
            width: 100%;
            margin: 10px 0;
          }

          .wangEditor-pc :deep(table td),
          .wangEditor-pc :deep(table th) {
            border: 1px solid #ccc;
            padding: 8px;
            text-align: left;
          }

          .wangEditor-pc :deep(table th) {
            background-color: #f5f5f5;
            font-weight: bold;
          }
        }
      }
    }
  }

  @media screen and (min-width: 750px) and (max-width: 1200px) {
    .main {
      padding: 0 1.2rem 0 2.4rem;

      .left-aside {
        width: 100%;
        max-width: 32rem;
      }

      .content {
        .aside {
          width: 100%;
          max-width: 32rem;

          .ul {
            transition: all 0.2s ease-in-out;
            overflow: hidden;

            .group {
              width: 100%;
              padding: 0 2.9rem;
              border-radius: 0.5rem;
              background: #f2f2f2;
              color: #000;
              font-size: 1.8rem;
              font-style: normal;
              font-weight: 500;
              line-height: normal;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }

            .li {
              width: 100%;
              padding: 1.2rem 2.9rem 1.2rem 4.8rem;
              border-radius: 0.5rem;
              font-size: 1.8rem;
              font-weight: 400;
              line-height: normal;
            }
          }
        }

        .article {
          .html {
            padding: 0 2.4rem 0 4.6rem;

            .wangEditor-pc :deep(h1) {
              font-size: 20px;
            }

            .wangEditor-pc :deep(h2) {
              font-size: 15px;
              font-weight: normal;
            }

            .wangEditor-pc :deep(h3) {
              font-size: 15px;
              font-weight: normal;
            }

            .wangEditor-pc :deep(h4) {
              font-size: 13px;
              font-weight: normal;
            }

            .wangEditor-pc :deep(h5) {
              font-size: 16px;
              font-weight: normal;
            }

            .wangEditor-pc :deep(p) {
              line-height: 1.8;
            }

            .wangEditor-pc :deep(a) {
              color: #1877f2;
            }
          }
        }
      }
    }
  }

  @media screen and (min-width: 1200px) {
    .main {
      .content {
        .article {
          .html {
            padding: 0 2.4rem 0 4.6rem;

            .wangEditor-pc :deep(h1) {
              font-size: 20px;
            }

            .wangEditor-pc :deep(h2) {
              font-size: 15px;
              font-weight: normal;
            }

            .wangEditor-pc :deep(h3) {
              font-size: 15px;
              font-weight: normal;
            }

            .wangEditor-pc :deep(h4) {
              font-size: 13px;
              font-weight: normal;
            }

            .wangEditor-pc :deep(h5) {
              font-size: 16px;
              font-weight: normal;
            }

            .wangEditor-pc :deep(p) {
              line-height: 1.8;
            }

            .wangEditor-pc :deep(a) {
              color: #1877f2;
            }
          }
        }
      }
    }
  }
}

// 图片预览蒙层样式
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: pointer;
  animation: fadeIn 0.3s ease-in-out;

  .image-preview-container {
    position: relative;
    max-width: 60%;
    max-height: 60%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: default;

    .image-preview-close {
      position: absolute;
      top: -6rem;
      right: -6rem;
      width: 4rem;
      height: 4rem;
      background-color: rgba(255, 255, 255, 0.9);
      border: none;
      border-radius: 50%;
      font-size: 3rem;
      color: #333;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      transition: all 0.3s ease;
      z-index: 10000;
      user-select: none;

      .close-img {
        width: 2.8rem;
        height: 2.8rem;
      }
    }

    .preview-image {
      max-width: 100%;
      max-height: 60vh;
      object-fit: contain;
      border-radius: 0.4rem;
      box-shadow: 0 0.4rem 2rem rgba(0, 0, 0, 0.5);
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}
</style>
